import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { FileSearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { Modal, Table, Form } from 'antd';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { ColSize, RowSize } from '../../../../../../../../../shared/components';
import { FloatCarsSummary } from '../FloatCarsSummary';
import { SectionQuotation, QUOTATION_BUTTON, StepOneFormContext, GenericDataFormContext, QuotationInfoContext } from '../../../../../shared';

import { StepThreeFormContext } from '../../contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';

import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';

import {
  displayWarningMessage,
  formattedPrice,
} from '../../../../../../../../../shared/utils';

import './index.css';
import { BranchDocumentsURL, BudgetType } from '../../../../../utils/quotation-documents.enum';
import { getQuoteDocumentPDF, transformToPdfQuote } from '../../../../../utils';
import { QuotationContextData } from '../../../../../../../../../shared/interfaces/services/quote-pdf-service';
import QuotationService from '../../../../../../../../../shared/services/quotation';
import { Article, GetArticleGroupsResponse } from '../../../../../../../../../shared/interfaces';

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Vehículo',
    dataIndex: 'vehicle',
  },
  {
    title: 'Cobertura',
    dataIndex: 'coverage',
  },
  {
    title: 'Suma asegurada',
    dataIndex: 'insuredAmount',
  },
  {
    title: 'Prima',
    dataIndex: 'prima',
  },
  {
    title: 'Comisión',
    dataIndex: 'commission',
  },
  {
    title: 'Premio',
    dataIndex: 'prize',
  },
];

export const CarQuotationSummary = () => {

  const { stepTwoForm, coverageSelectedByVehicle, stepThreeForm, quotationResponseByCar, quotationForm,reQuotationResponseByCar} = useContext(WizardDataFormContext);
  const { carFormValues, generateTitleForTabs, tabs, labelButton, activeKey, brandsByYear, modelsByBrandAndYear, versionsByYearBrandAndModel, scoringQuestions } = useContext(StepTwoFormContext);
  const { getInsuredSumaPerVehicle, } = useContext(StepThreeFormContext);

  const [canShowSummary, setCanShowSummary] = useState(false);
  const [allArticleSelected, setAllArticleSelected] = useState<Article | undefined>(undefined);

  const getVehicleDescription = useCallback(
    (componentId: string) => {
      const brand = stepTwoForm?.getFieldValue(`brand-tab-${componentId}`);
      const model = stepTwoForm?.getFieldValue(`model-tab-${componentId}`);
      const year = stepTwoForm?.getFieldValue(`vehicleYear-tab-${componentId}`);

      return generateTitleForTabs(year, brand, model);
    },
    [stepTwoForm, generateTitleForTabs]
  );

  const summaryTableDatasource = useMemo(
    () =>
      coverageSelectedByVehicle.map(({ componentId, coverageSelected }, i) => ({
        key: i,
        vehicle: getVehicleDescription(componentId),
        coverage: coverageSelected.descripcion,
        insuredAmount: `$${formattedPrice(
          String(getInsuredSumaPerVehicle(componentId))
        )}`,
        prima: `$${formattedPrice(coverageSelected.prima)}`,
        commission: `$${formattedPrice(
          coverageSelected.importesCobertura.importeComision
        )}`,
        prize: `$${formattedPrice(coverageSelected.premio)}`, // todo: creo que tiene que venir de un servicio..
      })),
    [coverageSelectedByVehicle, getVehicleDescription, getInsuredSumaPerVehicle]
  );

  const { stepOneForm, clientSelected } = useContext(StepOneFormContext);
  const { provinceList, allLocalitiesPerProvince, ivaConditionList, paymentMethods, paymentMethodsList, allCoveragesPerPlan} = useContext(GenericDataFormContext);
  const { quotationInfo, quoatationInfoRequest } = useContext(QuotationInfoContext);

  useEffect(() => {
    async function load() {
      if (!quotationInfo) return;
      const response: GetArticleGroupsResponse = (await QuotationService.getArticleGroups()).data;
      const foundArticle = findArticle(response, quotationInfo.groupSelected, quotationInfo.article);
      if (!allArticleSelected || allArticleSelected.codigo !== foundArticle?.codigo) {
        setAllArticleSelected(foundArticle);
      }
    }
    load();
  }, [quotationInfo, coverageSelectedByVehicle]);

  function findArticle(
    response: GetArticleGroupsResponse,
    groupCode?: string,
    articleCode?: string
  ): Article | undefined {
    if (!groupCode && !articleCode) return undefined;
    const group = response.Grupos.find(g => g.codigo === groupCode);
    if (!group) return undefined;

    return group.articulos.find(a => a.codigo === articleCode);
  }

  const ctx: QuotationContextData = {
    stepOneForm, quotationForm,
    clientSelected, allArticleSelected,
    stepTwoForm, stepThreeForm, carFormValues,
    quotationInfo, quotationResponseByCar, quoatationInfoRequest,reQuotationResponseByCar,
    activeKey,  coverageSelectedByVehicle,
    provinceList, allLocalitiesPerProvince, ivaConditionList,  paymentMethods, paymentMethodsList, allCoveragesPerPlan,
    brandsByYear, modelsByBrandAndYear, versionsByYearBrandAndModel,
    scoringQuestions,
    branchDocumentsURL: BranchDocumentsURL.AUTOMOVILES
  };

  return (
    <>
      <SectionQuotation level='h3' title='Resumen'>
        <RowSize>
          {tabs.length > 1 ? (
            <ColSize size={20}>
              <Form.Item label='Detalle'>
                <ActionButton
                  type='secondary'
                  color='secondaryDefault'
                  label={
                    labelButton === QUOTATION_BUTTON.RECOTIZAR
                      ? 'Recotizar'
                      : 'Resumen'
                  }
                  icon={
                    labelButton === QUOTATION_BUTTON.RECOTIZAR ? undefined : (
                      <FileSearchOutlined />
                    )
                  }
                  onClick={
                    labelButton === QUOTATION_BUTTON.RECOTIZAR
                      ? () =>
                          displayWarningMessage(
                            'Debe recotizar para poder ver el resumen.'
                          )
                      : () => setCanShowSummary(true)
                  }
                />
              </Form.Item>
            </ColSize>
          ) : (
            <></>
          )}

          <ColSize size={50}>
            <Form.Item label='Acciones'>
              <RowSize
                rowProps={{
                  gutter: [0, 15],
                  className: 'car-quotation-summary__container-action-buttons',
                }}
              >
                <ActionButton
                  className='btn-accent-outline-barbuss'
                  type='secondary'
                  label='Productor'
                  icon={<DownloadOutlined />}
                  onClick={() => getQuoteDocumentPDF(transformToPdfQuote(ctx, BudgetType.PRODUCTOR), BranchDocumentsURL.AUTOMOVILES)}
                />

                <ActionButton
                  className='btn-accent-outline-barbuss'
                  type='secondary'
                  color='secondaryDefault'
                  label='Cliente'
                  icon={<DownloadOutlined />}
                  onClick={() => getQuoteDocumentPDF(transformToPdfQuote(ctx, BudgetType.CLIENTE), BranchDocumentsURL.AUTOMOVILES)}
                />
              </RowSize>
            </Form.Item>
          </ColSize>
        </RowSize>
      </SectionQuotation>

      <Modal
        centered
        className='mt-1'
        footer={
          <ActionButton
            label='Cerrar'
            type='secondary'
            onClick={() => setCanShowSummary(false)}
            width={115}
          />
        }
        onCancel={() => setCanShowSummary(false)}
        open={canShowSummary}
        title='Detalle de cálculo'
        width={1000}
      >
        <Table
          bordered
          className='person-table mb-1'
          columns={MOCK_TABLE_COLUMNS_HEADER}
          dataSource={summaryTableDatasource}
          pagination={false}
          summary={() => <FloatCarsSummary />}
          scroll={{ x: 'auto' }}
        />
      </Modal>
    </>
  );
};
